import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import axios from "axios";
import { useNotification } from "../../../../context/NotificationContext";

const paymentModes = [
  { value: "CASH", label: "Cash" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "UPI", label: "UPI" },
  { value: "CARD", label: "Credit Card" },
  { value: "CHEQUE", label: "Cheque" },
];

const AddPaymentPopup = ({ onSubmit, bookingId, paymentData, isVisible,onCancel }) => {
  const [formValues, setFormValues] = useState({
    modeOfPayment: null,
    amount: "",
    reference: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false); // Track if payment data is initialized
  const submitButtonRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const isEditMode = !!paymentData;
  const { notify } = useNotification();

  useEffect(() => {
    if (isEditMode && paymentData) {
      setFormValues({
        modeOfPayment: paymentModes.find(mode => mode.value === paymentData.modeOfPayment) || null,
        amount: paymentData.amount?.toString() || "",
        reference: paymentData.reference || "",
      });
      setDataInitialized(true); // Mark data as initialized
    } else {
      setDataInitialized(true); // No data to initialize in add mode
    }
  }, [paymentData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormValues((prev) => ({
      ...prev,
      modeOfPayment: selectedOption,
    }));
    setErrors((prev) => ({ ...prev, modeOfPayment: "" }));
  };

  const validateForm = () => {
    let validationErrors = {};
    
    if (!formValues.modeOfPayment) validationErrors.modeOfPayment = "Mode of Payment is required";
    if (!formValues.amount || isNaN(parseFloat(formValues.amount)) || parseFloat(formValues.amount) <= 0) {
      validationErrors.amount = "Please enter a valid amount";
    }
    
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      notify("error", "Please fix the validation errors");
      return;
    }

    try {
      setLoading(true);
      const totalAmount = parseFloat(formValues.amount);

      const payload = {
        bookingId: bookingId,
        modeOfPayment: formValues.modeOfPayment.value,
        amount: parseFloat(formValues.amount),
        reference: formValues.reference,
        chargeId: isEditMode ? paymentData.chargeId : undefined, // Include chargeId for edit mode
      };

      const endpoint = isEditMode 
        ? `${apiUrl}/transactions/edit-payment` 
        : `${apiUrl}/transactions/add-payment`;

      const response = await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notify("success", `Payment ${isEditMode ? 'updated' : 'added'} successfully!`);
      if (onSubmit) onSubmit({ ...payload, totalAmount, charge_id: response.data.charge_id || payload.chargeId });
      onCancel();

      if (submitButtonRef.current) {
        submitButtonRef.current.click();
      }
    } catch (error) {
      notify("error", `Failed to ${isEditMode ? 'update' : 'add'} payment. Please try again.`);
      console.error(`Error ${isEditMode ? 'updating' : 'adding'} payment:`, error);
    } finally {
      setLoading(false);
    }
  };

  return (
     <div 
      className={`modal custom-modal ${isVisible ? "show" : ""}`}
      tabIndex="-1" 
      role="dialog"
      style={{ 
        display: isVisible ? "block" : "none",
        position: "fixed", 
        top: 0, 
        right: 0, 
        height: "100vh",
        width: "40%",
        zIndex: 1050,
      }}
    >
      <div
        className="modal-dialog modal-lg"
        style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "40%" }}
        role="document"
      >
        <form onSubmit={handleSubmit} style={{ height: "100%" }}>
          <div className="modal-content h-100">
            <div className="modal-header">
              <h5 className="modal-title">{isEditMode ? "Edit Payment" : "Add Payment"}</h5>
              <button 
                type="button" 
                className="btn-close" 
                data-bs-dismiss="modal" 
                aria-label="Close" 
                onClick={onCancel}
              />
            </div>

            <div className="modal-body" style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}>
              {dataInitialized ? (
                <>
                  <div className="mb-3">
                    <label className="form-label">Mode of Payment</label>
                    <Select
                      options={paymentModes}
                      onChange={handleSelectChange}
                      value={formValues.modeOfPayment}
                      isSearchable={false}
                    />
                    {errors.modeOfPayment && <small className="text-danger">{errors.modeOfPayment}</small>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Amount</label>
                    <input
                      type="number"
                      name="amount"
                      value={formValues.amount}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Enter amount"
                    />
                    {errors.amount && <small className="text-danger">{errors.amount}</small>}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Reference</label>
                    <input
                      type="text"
                      name="reference"
                      value={formValues.reference}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Enter reference (optional)"
                    />
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <div className="loader">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "none" }}>
              <button ref={submitButtonRef} data-bs-dismiss="modal" aria-label="Close" type="reset">
                Submit2
              </button>
            </div>

            <div className="modal-footer">
              <button type="submit" className="btn btn-primary" disabled={loading || !dataInitialized}>
                {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary" 
                data-bs-dismiss="modal"
                onClick={onCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPaymentPopup;